import React, { useCallback, useRef, useState } from 'react'
import { scan } from '../lib/api'
import { extractCardNumbers } from '../lib/ocr'
import type { ScanResponse } from '../types/card'
import ResultsList from './ResultsList'

type Status =
  | { kind: 'idle' }
  | { kind: 'loading'; label: string }
  | { kind: 'ready'; response: ScanResponse; photo: string; file: File; ocr: { numbers: string[]; raw: string; crop_data_url: string } }
  | { kind: 'error'; message: string }

export default function Scanner() {
  const [status, setStatus] = useState<Status>({ kind: 'idle' })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(async (file: File) => {
    const photo = URL.createObjectURL(file)
    try {
      setStatus({ kind: 'loading', label: 'reading card number (OCR)…' })
      const ocr = await extractCardNumbers(file)
      setStatus({
        kind: 'loading',
        label: ocr.numbers.length
          ? `found ${ocr.numbers.join(', ')} — matching…`
          : 'no card number found — full pHash search…',
      })
      const response = await scan(file, ocr.numbers)
      setStatus({ kind: 'ready', response, photo, file, ocr })
    } catch (err: any) {
      setStatus({ kind: 'error', message: err?.message ?? String(err) })
    }
  }, [])

  const handleManualNumber = useCallback(async (manualNumber: string) => {
    if (status.kind !== 'ready') return
    const cleaned = manualNumber.trim().toUpperCase()
    if (!cleaned) return
    const { file, photo, ocr } = status
    setStatus({ kind: 'loading', label: `re-narrowing on ${cleaned}…` })
    try {
      const response = await scan(file, [cleaned])
      setStatus({
        kind: 'ready',
        response,
        photo,
        file,
        ocr: { ...ocr, numbers: [cleaned] },
      })
    } catch (err: any) {
      setStatus({ kind: 'error', message: err?.message ?? String(err) })
    }
  }, [status])

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFile(f)
  }

  const reset = () => {
    setStatus({ kind: 'idle' })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  if (status.kind === 'ready') {
    const { response, photo, ocr } = status
    return (
      <div className="flex-1 flex flex-col">
        <div className="p-3 flex items-center justify-between border-b border-edge">
          <div className="text-sm">
            <span className="text-white">{response.cards.length}</span>
            <span className="text-muted"> card{response.cards.length === 1 ? '' : 's'} · </span>
            <span className="text-muted">{response.timings_ms.total}ms</span>
            {ocr.numbers.length > 0 ? (
              <span className="text-muted"> · OCR: <span className="text-emerald-300 font-mono">{ocr.numbers.join(', ')}</span></span>
            ) : (
              <span className="text-muted"> · OCR: <span className="text-amber-300">no card number</span></span>
            )}
          </div>
          <button
            onClick={reset}
            className="text-xs px-3 py-1.5 rounded-md bg-accent text-ink font-medium"
          >
            scan another
          </button>
        </div>
        <ResultsList
          response={response}
          photo={photo}
          ocrRaw={ocr.raw}
          ocrCropDataUrl={ocr.crop_data_url}
          onManualNumber={handleManualNumber}
        />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onSelect}
      />

      {status.kind === 'loading' ? (
        <>
          <div className="text-muted text-sm mb-3">{status.label}</div>
          <div className="w-40 h-1 rounded-full bg-edge overflow-hidden">
            <div className="h-full bg-accent animate-pulse w-full" />
          </div>
        </>
      ) : (
        <>
          <div className="w-20 h-28 rounded-lg border-2 border-dashed border-edge mb-6 flex items-center justify-center text-3xl text-muted">
            ◎
          </div>
          <h2 className="text-xl font-semibold mb-2">Scan a card</h2>
          <p className="text-muted text-sm max-w-xs mb-6">
            Snap or upload a photo. OCR reads the card number (OP09-065 etc.) to narrow the candidates; pHash picks the right variant.
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 rounded-lg bg-accent text-ink font-semibold"
          >
            Choose photo
          </button>
          <p className="text-[10px] text-muted mt-3">
            mobile: opens camera or library · desktop: file picker
          </p>
          {status.kind === 'error' && (
            <p className="mt-6 text-sm text-red-400 max-w-xs whitespace-pre-wrap">
              {status.message}
            </p>
          )}
        </>
      )}
    </div>
  )
}
